/***************************************************
 ****** PROCESS MODELS API HELPERS *****************
 ****** Thin wrappers around the processmodelsApi
 ****** axios instance for endpoints that don't have
 ****** a Redux slice / hook yet.
 ***************************************************/
import { AxiosError } from "axios";
import { processmodelsApi } from "api";

export type ActiveProcess = {
  id: number;
  name: string;
  // User currently assigned to the active process. Optional because
  // the backend may return null/empty when the process is not yet
  // assigned to anyone (e.g. just started and waiting for a claim).
  // The backend nests the user fields under `user`; the flat fields
  // are kept for backwards compatibility with older responses.
  firstname?: string | null;
  lastname?: string | null;
  user?: {
    firstname?: string | null;
    lastname?: string | null;
  } | null;
};

export type ActiveProcessesResponse = {
  count: number;
  activeprocesses: ActiveProcess[];
};

/**
 * GET /processmodels/{id}/active-processes
 *
 * Returns the list of process tokens currently active (state ≠ Done)
 * for the given model within the caller's organization. The
 * endpoint is expected to be provided by the backend; while it's
 * not available, this helper returns `null` (caller treats that as
 * "skip the warning" — fail-safe, no UX regression).
 *
 * `params.raise = false` opts out of the global error banner so a
 * 404 from a backend that doesn't yet expose the endpoint doesn't
 * surface to the user.
 */
export const getActiveProcesses = async (
  modelId: number
): Promise<ActiveProcessesResponse | null> => {
  try {
    const { data } = await processmodelsApi.get<ActiveProcessesResponse>(
      `/${modelId}/active-processes`,
      { params: { raise: false } }
    );
    if (typeof data?.count !== "number" || !Array.isArray(data?.activeprocesses)) {
      return null;
    }
    return data;
  } catch (e) {
    const status = (e as AxiosError)?.response?.status;
    // 404 = endpoint not deployed yet. Any other error → skip the
    // warning so the existing depublish flow is preserved.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        `[getActiveProcesses] failed (status=${status}); skipping warning.`
      );
    }
    return null;
  }
};
